import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { Modal, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import React from "react";
import type { ServiceStandardMappingDTO } from "../api/client";
import {
  useCreateServiceStandardMapping,
  useServiceStandardMappingsList,
  useServiceStandardQuery,
  useUpdateServiceStandardMapping,
} from "../api/queries";
import {
  ServiceStandardMappingTour,
  useServiceStandardMappingTour,
} from "../tour/ServiceStandardMappingTour";
import { ServiceStandardMappingFilter } from "../ui/ServiceStandardMappingFilter";
import { ServiceStandardMappingForm } from "../ui/ServiceStandardMappingForm";
import { ServiceStandardMappingTable } from "../ui/ServiceStandardMappingTable";
import { ServiceStandardMappingToolbar } from "../ui/ServiceStandardMappingToolbar";

type ServiceStandardMappingQueryDTO = {
  offset?: number;
  limit?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  filters?: Array<{ field: string; op: string; value: unknown }>;
  serviceStandardId?: string;
  positionId?: string;
  checklistId?: string;
  qcStandardId?: string;
  kpiId?: string;
  search?: string;
};

export function ServiceStandardMappingPage() {
  const navigate = useNavigate({ from: "/app/service-standard-mapping" });
  const routerSearch = useSearch({
    from: "/app/service-standard-mapping",
    strict: false,
  });
  const tour = useServiceStandardMappingTour();
  const search: Partial<ServiceStandardMappingQueryDTO> = {
    offset: (routerSearch.offset as number | undefined) ?? 0,
    limit: (routerSearch.limit as number | undefined) ?? 20,
    serviceStandardId: routerSearch.serviceStandardId as string | undefined,
    positionId: routerSearch.positionId as string | undefined,
    checklistId: routerSearch.checklistId as string | undefined,
    qcStandardId: routerSearch.qcStandardId as string | undefined,
    kpiId: routerSearch.kpiId as string | undefined,
    search: routerSearch.search as string | undefined,
    sort: routerSearch.sort as
      | Array<{ field: string; dir: "asc" | "desc" }>
      | undefined,
  };

  const list = useServiceStandardMappingsList({
    offset: search.offset ?? 0,
    limit: search.limit ?? 20,
    sort: search.sort,
    filters: search.filters,
    serviceStandardId: search.serviceStandardId,
    positionId: search.positionId,
    checklistId: search.checklistId,
    qcStandardId: search.qcStandardId,
    kpiId: search.kpiId,
    search: search.search,
  });

  const canManage = useActionPermission(["service_standard:create"]);

  const {
    isOpen: isCreateOpen,
    open: onCreateOpen,
    close: onCreateClose,
  } = useDisclosure();
  const {
    isOpen: isEditOpen,
    open: onEditOpen,
    close: onEditClose,
  } = useDisclosure();
  const [editingMapping, setEditingMapping] =
    React.useState<ServiceStandardMappingDTO | null>(null);
  const [selectedServiceStandardId, setSelectedServiceStandardId] =
    React.useState<string>("");

  // Get service standard details when editing
  const serviceStandardQuery = useServiceStandardQuery(
    selectedServiceStandardId,
  );

  const [createServiceStandardId, setCreateServiceStandardId] =
    React.useState<string>("");
  const createMapping = useCreateServiceStandardMapping(
    createServiceStandardId,
  );

  const handleCreate = (values: {
    serviceStandardId?: string;
    positionId?: string | null;
    checklistId?: string | null;
    qcStandardId?: string | null;
    kpiId?: string | null;
  }) => {
    if (!values.serviceStandardId) {
      toast.error("ກະລຸນາເລືອກມາດຕະຖານບໍລິການ");
      return;
    }
    setCreateServiceStandardId(values.serviceStandardId);
    createMapping.mutate(
      {
        positionId: values.positionId,
        checklistId: values.checklistId,
        qcStandardId: values.qcStandardId,
        kpiId: values.kpiId,
      },
      {
        onSuccess: () => {
          onCreateClose();
          setCreateServiceStandardId("");
          list.refetch();
        },
      },
    );
  };

  const handleEdit = (mapping: ServiceStandardMappingDTO) => {
    setEditingMapping(mapping);
    setSelectedServiceStandardId(mapping.serviceStandardId);
    onEditOpen();
  };

  const updateMapping = useUpdateServiceStandardMapping(
    editingMapping?.serviceStandardId ?? "",
  );

  const handleUpdate = (values: {
    positionId?: string | null;
    checklistId?: string | null;
    qcStandardId?: string | null;
    kpiId?: string | null;
  }) => {
    if (!editingMapping) return;
    updateMapping.mutate(
      { id: editingMapping.id, data: values },
      {
        onSuccess: () => {
          onEditClose();
          setEditingMapping(null);
          setSelectedServiceStandardId("");
          list.refetch();
        },
      },
    );
  };

  const handleDelete = async (mapping: ServiceStandardMappingDTO) => {
    const { serviceStandardMappingApi } = await import("../api/client");
    await toast.promise(serviceStandardMappingApi.deleteMapping(mapping.id), {
      loading: "ກຳລັງລຶບ...",
      success: "ລຶບການເຊື່ອມຕໍ່ສຳເລັດ",
      error: "ລຶບການເຊື່ອມຕໍ່ລົ້ມເຫຼວ",
    });
    list.refetch();
  };

  const handleFiltersChange = (
    newFilters: Partial<ServiceStandardMappingQueryDTO>,
  ) => {
    navigate({
      search: {
        ...routerSearch,
        ...newFilters,
        offset: 0,
      },
    });
  };

  return (
    <>
      <Header />
      <Main>
        <ServiceStandardMappingTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <ServiceStandardMappingToolbar
          canManage={!!canManage}
          onCreate={onCreateOpen}
          onStartTour={tour.startTour}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <ServiceStandardMappingFilter
            filters={search}
            onFiltersChange={handleFiltersChange}
          />

          <ServiceStandardMappingTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) => {
              navigate({
                search: {
                  ...routerSearch,
                  offset,
                  limit,
                },
              });
            }}
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) => {
              navigate({
                search: {
                  ...routerSearch,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              });
            }}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </div>

        {/* Create Mapping Modal */}
        <Modal
          open={isCreateOpen}
          onOpenChange={(open) => {
            if (!open) onCreateClose();
          }}
          title="ສ້າງການຜູກມາດຕະຖານບໍລິການໃໝ່"
        >
          <ServiceStandardMappingForm
            onSubmit={handleCreate}
            requireServiceStandard={true}
            submitting={createMapping.isPending}
          />
        </Modal>

        {/* Edit Mapping Modal */}
        <Modal
          open={isEditOpen}
          onOpenChange={(open) => {
            if (!open) {
              onEditClose();
              setEditingMapping(null);
              setSelectedServiceStandardId("");
            }
          }}
          title="ແກ້ໄຂການຜູກມາດຕະຖານບໍລິການ"
        >
          {editingMapping && serviceStandardQuery.data && (
            <ServiceStandardMappingForm
              initialValues={{
                positionId: editingMapping.positionId ?? null,
                checklistId: editingMapping.checklistId ?? null,
                qcStandardId: editingMapping.qcStandardId ?? null,
                kpiId: editingMapping.kpiId ?? null,
              }}
              onSubmit={handleUpdate}
              submitting={updateMapping.isPending}
              serviceStandardDepartmentId={
                serviceStandardQuery.data?.departmentId
              }
            />
          )}
        </Modal>
      </Main>
    </>
  );
}
